import { useEffect, useState } from 'react';
import { api, type AgentSummary, type Capabilities, type Me } from '../api';
import { Chip, Empty, ErrorNote, Loading, useLoad } from '../components/ui';
import TalkCard from '../components/TalkCard';
import CallCard from '../components/CallCard';
import VoiceCompare from '../components/VoiceCompare';

/** The page a client sees: pick an agent, then talk in the browser or get a call. */
export default function Try({ me }: { me: Me }) {
  const agents = useLoad(() => api.get<{ agents: AgentSummary[] }>('/api/agents'));
  const caps = useLoad(() => api.get<Capabilities>('/api/capabilities'));
  const [agentId, setAgentId] = useState('');

  const list = (agents.data?.agents ?? []).filter((a) => a.published || a.mine || me.role === 'owner');
  useEffect(() => {
    if (!agentId && list.length) setAgentId(list[0].id);
  }, [list, agentId]);
  const agent = list.find((a) => a.id === agentId);

  if (agents.loading && !agents.data) return <Loading />;
  if (agents.error) return <ErrorNote error={agents.error} />;
  if (!list.length) return <Empty>No agent is published yet. {me.role === 'owner' ? 'Publish one under Agents.' : 'Please check back soon.'}</Empty>;

  return (
    <>
      <div className="hero">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>{me.teammateOf ? `Testing as ${me.teammateOf}` : 'Live AI voice agent'}</div>
          <h1>Talk to {agent?.name.split('·')[0].trim() ?? 'the agent'}.</h1>
          <p className="sub">
            Speak naturally in English, Hindi or Hinglish, and interrupt whenever you like. Try it right here in your browser, or have it
            call your phone.
          </p>
        </div>
        <div className="hero-meta" aria-label="Technology">
          <Chip tone="neutral">Deepgram Nova-3</Chip>
          <Chip tone="neutral">{agent?.brain ?? 'Groq'}</Chip>
          <Chip tone="accent">{agent?.voice ?? 'Rumik'}</Chip>
        </div>
      </div>

      {list.length > 1 ? (
        <div className="field" style={{ maxWidth: 520 }}>
          <label htmlFor="agent">Agent</label>
          <select id="agent" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            {list.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}{a.mine ? ' (yours)' : a.published ? '' : ' (unpublished)'}
              </option>
            ))}
          </select>
          {agent?.description ? <div className="hint">{agent.description}</div> : null}
        </div>
      ) : agent?.description ? (
        <p className="muted" style={{ marginTop: -8 }}>{agent.description}</p>
      ) : null}

      <div className="grid-2" style={{ gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)', alignItems: 'start' }}>
        <TalkCard agentId={agentId} agentName={agent?.name.split('·')[0].trim() ?? 'Agent'} maxSeconds={caps.data?.limits?.maxWebSeconds ?? null} />
        <div className="stack">
          <CallCard agentId={agentId} caps={caps.data} onUsed={caps.reload} />
          {caps.data?.voiceCompare ? <VoiceCompare /> : null}
        </div>
      </div>
      <style>{`@media (max-width: 1000px) { .grid-2 { grid-template-columns: minmax(0, 1fr) !important; } }`}</style>
    </>
  );
}
